import {
    Card,
    CardHeader,
    Divider,
    Text,
    mergeClasses,
} from '@fluentui/react-components';
import { useMetricsTileStyles } from './MetricsTile.styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from '../../../../../../../store/hooks';
import ClearFilterButton from '../../../../../../../controls/Buttons/ClearFilterButton';

export interface IMetricsTileBaseProps {
    title: React.ReactNode;
    titleActions?: React.ReactNode;
    hidePreviousRange?: boolean;
    vertical?: boolean;
    className?: string;
    reversed?: boolean;
    filter?: {
        value?: string | null;
        clear?: () => void;
    };
}

export type MetricsTileComponent<P = {}> = React.FC<P & IMetricsTileBaseProps>;

export interface IMetricsTileProps extends IMetricsTileBaseProps {
    previousRangeItem?: React.ReactElement;
    currentRangeItem?: React.ReactElement;
}

const RangeLabel: React.FC<{ value: string }> = ({ value }) => {
    return <Text size={200}>{value}</Text>;
};

const NoDataLabel: React.FC = () => {
    const { t } = useTranslation();
    const styles = useMetricsTileStyles();

    return (
        <Text weight="bold" className={styles.noDataLabel}>
            {t('ReportingDashboard_NoDataLabel')}
        </Text>
    );
};

const MetricsTile: React.FC<IMetricsTileProps> = ({
    title,
    titleActions,
    previousRangeItem,
    currentRangeItem,
    hidePreviousRange,
    className,
    vertical,
    reversed,
    filter,
}) => {
    const styles = useMetricsTileStyles();
    const { t } = useTranslation();

    const { rangeLabels } = useSelector((state) => state.reportingDashboard);

    return (
        <Card
            appearance="outline"
            className={mergeClasses(
                styles.root,
                className,
                filter?.value ? styles.hightlighted : ''
            )}
        >
            <CardHeader
                header={
                    <div className={styles.header}>
                        <div className={styles.headerRow}>
                            {
                                <Text weight="bold" size={500}>
                                    {title}
                                </Text>
                            }
                            {titleActions}
                        </div>
                        {filter?.value && (
                            <>
                                <Divider />
                                <div className={styles.headerRow}>
                                    <div>
                                        <Text size={400}>{t('Filter')}: </Text>
                                        <Text weight="semibold" size={400}>
                                            {filter.value}
                                        </Text>
                                    </div>
                                    {filter.clear && (
                                        <ClearFilterButton
                                            onClick={filter.clear}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                }
            />
            <div
                className={mergeClasses(
                    styles.content,
                    vertical ? 'vertical' : '',
                    reversed ? 'reversed' : ''
                )}
            >
                {!hidePreviousRange && (
                    <div className={styles.valueContainer}>
                        <RangeLabel value={rangeLabels.previousDescriptive} />
                        {previousRangeItem || <NoDataLabel />}
                    </div>
                )}
                <div
                    className={mergeClasses(
                        styles.valueContainer,
                        styles.currentHighlight
                    )}
                >
                    <RangeLabel value={rangeLabels.currentDescriptive} />
                    {currentRangeItem || <NoDataLabel />}
                </div>
            </div>
        </Card>
    );
};

export default MetricsTile;
